import React from 'react';

/**
 * Centered overlay shown ON TOP OF a <video> element when the backend
 * has flagged an R2 outage (503 + code: 'R2_OUTAGE'). Tells the viewer
 * what's happening, ticks down to an auto-retry, and exposes a manual
 * Retry button.
 *
 * Designed to sit inside the same positioned container as the <video>
 * (parent should be `position: relative`). The overlay itself is
 * `position: absolute` and fills its parent.
 *
 * Why a custom overlay instead of letting <video> show its broken
 * state: in a prospect-facing context (Share page, Decision Room),
 * a black frame with a tiny "Video can't play" message reads as
 * "this seller's product is broken." A polished overlay explaining
 * "storage hiccup, retrying in 47s" preserves trust during a
 * transient incident.
 */
function VideoOutageOverlay({ secondsLeft, message, onRetry }) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(15, 23, 42, 0.78)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        color: 'var(--room-surface, #f8fafc)',
        padding: 24,
        textAlign: 'center',
        zIndex: 5,
        borderRadius: 'inherit',
      }}
    >
      <div style={{ maxWidth: 420 }}>
        <div
          aria-hidden="true"
          style={{
            width: 48,
            height: 48,
            margin: '0 auto 16px',
            borderRadius: '50%',
            background: 'rgba(251, 191, 36, 0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
          Video temporarily unavailable
        </div>
        <div style={{ fontSize: 14, color: 'var(--room-border, #cbd5e1)', lineHeight: 1.5, marginBottom: 16 }}>
          {message || "We're having a transient hiccup pulling this from storage. It usually clears in under a minute."}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onRetry}
            style={{
              background: '#0891b2',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Retry now
          </button>
          {secondsLeft > 0 && (
            <span style={{ fontSize: 13, color: 'var(--room-text-subtle, #94a3b8)' }}>
              Auto-retry in {secondsLeft}s
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default VideoOutageOverlay;
